<template>
    <div class="select-input-wrapper">
        <div class="input-search-group" :class="{ active: isActive }">
            <div class="tag-list" @click.stop="focusInput">
                <span
                    v-for="item in selectedItems"
                    :key="item.value"
                    class="tag"
                    :title="'点击取消选择'"
                    @click.stop="removeTag(item)"
                >
                    <slot name="tag" :item="item">
                        {{ item.text }}
                    </slot>
                    <span class="tag-close" @click.stop="removeTag(item)">×</span>
                </span>
                <input
                    ref="input"
                    v-model="inputValue"
                    class="select-input"
                    :placeholder="placeholder"
                    autocomplete="off"
                    @input="onInputActive"
                    @keydown.enter.prevent="onEnter"
                    @focus="onFocus"
                    @blur="onBlur"
                    @keydown.down.prevent="moveActive(1)"
                    @keydown.up.prevent="moveActive(-1)"
                    @keydown.esc="showDropdown = false"
                >
            </div>
            <button class="search-btn" @mousedown.prevent="onSearch">
                <v-icon name="basic_search_line" remote />
            </button>
        </div>
        <ul v-if="showDropdown && filteredOptions.length" class="dropdown">
            <li
                v-for="(item, idx) in filteredOptions"
                :key="item.value + idx"
                :class="{
                    active: idx === activeIndex,
                    selected: isSelected(item),
                }"
                @mousedown.prevent="toggleSelectItem(item)"
                @mouseenter="activeIndex = idx"
            >
                <slot name="item" :item="item">
                    {{ item.text }}
                </slot>
            </li>
        </ul>
    </div>
</template>

<script>
export default {
    name: 'SelectInput',
    props: {
        value: {
            type: [Array, String, Number],
            default: () => [],
        },
        options: {
            type: Array,
            default: () => [],
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        fetchOptions: {
            type: Function,
            default: null,
        },
        allowCreate: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            inputValue: '',
            showDropdown: false,
            innerOptions: [],
            activeIndex: 0,
            isActive: false,
        };
    },
    computed: {
        selectedItems() {
            if (this.multiple) {
                // value 里每个都找 innerOptions 里有没有，没有就用 { text: value, value }
                return (Array.isArray(this.value) ? this.value : []).map(val => this.innerOptions.find(opt => opt.value === val) || {
                            text: val,
                            value: val,
                        });
            }
            const v = this.value;
            if (v == null || v === '') return [];
            return [
                this.innerOptions.find(opt => opt.value === v) || {
                    text: v,
                    value: v,
                },
            ];
        },
        filteredOptions() {
            return this.innerOptions.filter(opt => String(opt.text)
                    .toLowerCase()
                    .includes(this.inputValue.toLowerCase()));
        },
        optionExists() {
            return this.innerOptions.some(opt => opt.text === this.inputValue);
        },
    },
    watch: {
        options: {
            handler(val) {
                this.innerOptions = val;
            },
            immediate: true,
        },
        value: {
            handler() {},
            immediate: true,
        },
    },
    methods: {
        focusInput() {
            this.$refs.input && this.$refs.input.focus();
        },
        onInputActive(e) {
            this.onInput(e);
            this.isActive = true;
        },
        onFocus() {
            this.isActive = true;
            this.showDropdown = true;
        },
        onBlur() {
            setTimeout(() => {
                this.showDropdown = false;
                this.isActive = false;
                this.inputValue = '';
            }, 200);
        },
        onInput() {
            this.showDropdown = true;
            if (this.fetchOptions) {
                this.fetchOptions(this.inputValue).then((list) => {
                    this.innerOptions = list;
                });
            }
        },
        isArrayEqualUnordered(arr1, arr2) {
            return (
                arr1.length === arr2.length
                && new Set(arr1).size === arr1.length
                && new Set(arr2).size === arr2.length
                && arr1.every(v => arr2.includes(v))
            );
        },
        onSearch(e) {
            e.preventDefault();
            e.stopPropagation();
            this.showDropdown = false;
            // 搜索按钮点击，触发事件
            this.$emit('search', this.value);
            // 也可添加/选择当前输入内容
            if (this.inputValue) {
                if (this.allowCreate && !this.optionExists) {
                    this.createAndSelect();
                } else if (this.filteredOptions[this.activeIndex]) {
                    this.toggleSelectItem(this.filteredOptions[this.activeIndex]);
                }
            }
            if (this.fetchOptions) {
                this.fetchOptions('').then((list) => {
                    this.innerOptions = list;
                });
            }
        },
        onEnter() {
            if (!this.inputValue) return;
            // 检查是否已存在于已选
            if (
                Array.isArray(this.value)
                && this.value.includes(this.inputValue)
            ) {
                this.$message.error('已存在');
                this.inputValue = '';
                return;
            }
            if (!this.allowCreate) {
                this.inputValue = '';
                return;
            }
            const found = this.filteredOptions.find(opt => opt.text === this.inputValue);
            if (found) {
                this.toggleSelectItem(found);
            } else if (this.allowCreate) {
                this.createAndSelect();
            }
        },
        toggleSelectItem(item) {
            if (this.isSelected(item)) {
                this.removeTag(item);
            } else {
                this.selectItem(item);
            }
        },
        selectItem(item) {
            if (this.multiple) {
                // 去重：如果已存在则不添加
                if (
                    Array.isArray(this.value)
                    && this.value.includes(item.value)
                ) {
                    this.inputValue = '';
                    return;
                }
                const newValue = Array.isArray(this.value)
                    ? [...this.value]
                    : [];
                newValue.push(item.value);
                this.$emit('input', newValue);
                this.$emit('change', newValue);
                this.inputValue = '';
            } else {
                this.$emit('input', item.value);
                this.$emit('change', item.value);
                this.inputValue = item.text;
                this.showDropdown = false;
            }
        },
        removeTag(item) {
            if (this.multiple) {
                const newValue = this.value.filter(val => val !== item.value);
                this.$emit('input', newValue);
                this.$emit('change', newValue);
            }
        },
        isSelected(item) {
            if (this.multiple) {
                return (
                    Array.isArray(this.value) && this.value.includes(item.value)
                );
            }
            return this.value === item.value;
        },
        createAndSelect() {
            // 检查是否已存在于已选
            if (
                Array.isArray(this.value)
                && this.value.includes(this.inputValue)
            ) {
                this.inputValue = '';
                return;
            }
            // 检查是否已存在于 options
            if (
                this.innerOptions.some(opt => opt.value === this.inputValue)
            ) {
                this.inputValue = '';
                return;
            }
            const newItem = { text: this.inputValue, value: this.inputValue };
            this.innerOptions.push(newItem);
            this.selectItem(newItem);
            this.inputValue = '';
        },
        moveActive(step) {
            if (!this.showDropdown) {
                this.showDropdown = true;
                return;
            }
            const len = this.filteredOptions.length;
            if (len === 0) return;
            this.activeIndex = (this.activeIndex + step + len) % len;
        },
    },
};
</script>

<style scoped>
.select-input-wrapper {
    position: relative;
    min-width: 260px;
    display: inline-block;
}
.input-search-group {
    display: flex;
    align-items: center;
    border: 1px solid rgba(17, 32, 70, 0.13);
    border-radius: 3px;
    background-color: #fff;
    box-sizing: border-box;
}
.tag-list {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    flex: 1;
    min-width: 0;
}

.tag {
    border-radius: 3px;
    padding: 2px 8px;
    margin: 2px 0 2px 4px;
    font-size: 12px;
    line-height: 16px;
    color: rgba(74, 112, 255, 1);
    display: flex;
    align-items: center;
    border: 1px solid #dbe8ff;
    background-color: #f1f6ff;
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s;
}
.tag-close {
    margin-left: 4px;
    cursor: pointer;
    color: rgba(74, 112, 255, 1);
    font-size: 14px;
}
.select-input {
    width: 100px;
    border: none;
    outline: none;
    font-size: 12px;
    height: 30px;
    line-height: 30px;
    padding: 0 8px;
    background: transparent;
    font: var(--body-regular);
    margin-top: -1px;
    &::placeholder {
        color: var(--color-text-caption);
    }
    &::-webkit-input-placeholder {
        color: var(--color-text-caption);
    }
    &:-ms-input-placeholder {
        color: var(--color-text-caption);
    }
    &::-ms-input-placeholder {
        color: var(--color-text-caption);
    }
}
.search-btn {
    background: none;
    border: none;
    outline: none;
    cursor: pointer;
    padding: 0 8px;
    height: 30px;
    display: flex;
    align-items: center;
    &:active {
        background-color: #4a70ff;
        color: #fff;
    }
}

.input-search-group.active {
    border: 1px solid #4a70ff;
    .search-btn {
        background-color: #4a70ff;
        color: #fff;
    }
}
.input-search-group:active,
.input-search-group:focus {
    border: 1px solid #4a70ff;
}

.dropdown {
    position: absolute;
    left: 0;
    top: 100%;
    background-color: #fff;
    border: 1px solid #e6e6e6;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    z-index: 100;
    max-height: 180px;
    overflow-y: auto;
    margin-top: 4px;
    padding: 4px;
    list-style: none;
    min-width: 100%;
}
.dropdown li {
    padding: 4px 8px;
    cursor: pointer;
    color: rgba(0, 1, 10, 0.93);
    font-size: 14px;
}
.dropdown li:hover,
.dropdown li.selected {
    background-color: #f3f5fa;
}
.dropdown-create {
    color: rgba(74, 112, 255, 1);
    font-weight: bold;
}
</style>
